import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Space, Button, message } from "@mimber/ui-web"
import { Form, InputField, RichTextEditorField } from "@mimber/forms"
import { useCreateCommunicationTemplate } from "@mimber/client/mimber-organization"
import { CommunicationTemplateStatus } from "@mimber/schema"
import {
  QuickCreateCommunicationTemplateFormSchema,
  type QuickCreateCommunicationTemplateFormData,
} from "./QuickCreateCommunicationTemplateForm.schema"
import type { QuickCreateCommunicationTemplateFormProps } from "./QuickCreateCommunicationTemplateForm.types"

export const QuickCreateCommunicationTemplateForm: React.FunctionComponent<
  QuickCreateCommunicationTemplateFormProps
> = ({ channel, onSuccess, onCancel }) => {
  const form = useForm<QuickCreateCommunicationTemplateFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(QuickCreateCommunicationTemplateFormSchema) as any,
    mode: "onBlur",
    defaultValues: {
      subject: "",
      content: "",
    },
  })

  const {
    reset,
  } = form

  const mutation = useCreateCommunicationTemplate({
    onSuccess: (response) => {
      message.success("Template created successfully")
      reset()
      onSuccess?.(response.data)
    },
    onError: (error) => {
      message.error(`Failed to create template: ${error.message}`)
    },
  })

  const onSubmit = (data: QuickCreateCommunicationTemplateFormData) => {
    const truncatedSubject = data.subject.slice(0, 244)
    mutation.mutate({
      name: `${truncatedSubject} Template`,
      subject: data.subject,
      content: data.content,
      channel,
      status: CommunicationTemplateStatus.ACTIVE,
    })
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={mutation.isPending}
      layout="vertical"
    >
      <InputField
        name="subject"
        label="Subject"
        placeholder="e.g., Monthly Newsletter, Event Reminder"
        required
        maxLength={255}
        showCount
      />

      <RichTextEditorField
        name="content"
        label="Content"
        placeholder="Write your template content here..."
        required
        minHeight={200}
      />

      <Space style={{ justifyContent: "flex-end", width: "100%" }}>
        <Button onClick={onCancel} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={mutation.isPending}

        >
          Create &amp; Select
        </Button>
      </Space>
    </Form>
  )
}
